import { useCallback, useEffect, useMemo } from 'react';
import { Form, message } from 'antd';
import type { Dayjs } from 'dayjs';
import dayjs from 'dayjs';
import { useClosingPackageCommands } from './useClosingPackageCommands';
import { useClosingPackageTaskModalData } from './useClosingPackageTaskModalData';
import { buildCompositionEditorRows } from '../lib/compositionEditorAdapter';
import { resolveCompositionEditGate } from '../lib/compositionEditGate';
import {
  buildFormationConfirmBody,
  validateFormationSubmit,
} from '../lib/formationModalValidation';
import { mapClosingPackageApiError } from '../lib/errorMapping';
import { useClosingPackagePermissions } from './useClosingPackagePermissions';
import { useSelector } from 'react-redux';
import type { RootState } from '../../../store';
import { userHasClosingPackageRole } from '../permissions/closingPackagePermissions';

export type FormationModalFormValues = {
  formedAt: Dayjs;
  comment: string;
};

export type UseFormationModalOptions = {
  applicationId: number;
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void | Promise<void>;
};

export function useFormationModal({
  applicationId,
  open,
  onClose,
  onSuccess,
}: UseFormationModalOptions) {
  const [form] = Form.useForm<FormationModalFormValues>();
  const permissions = useClosingPackagePermissions();
  const user = useSelector((state: RootState) => state.auth.user);

  const { projection, composition, contextView, contextLoading } = useClosingPackageTaskModalData({
    applicationId,
    open,
    loadComposition: true,
  });

  const { confirmFormation, states } = useClosingPackageCommands();

  const allowedToEdit = useMemo(
    () => permissions.canEdit && userHasClosingPackageRole(user, ['admin', 'accountant']),
    [permissions.canEdit, user]
  );

  const validationRows = useMemo(() => {
    if (!projection) return [];
    const editGate = resolveCompositionEditGate({
      allowedToEdit,
      locked: projection.locked,
      formed: projection.formed,
      compositionMode: projection.compositionMode,
      workflowState: projection.workflowState,
    });
    return buildCompositionEditorRows({
      projection,
      composition: composition ?? null,
      canEdit: editGate.canEdit,
    });
  }, [allowedToEdit, composition, projection]);

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    form.setFieldsValue({
      formedAt: dayjs(),
      comment: '',
    });
  }, [form, open, applicationId]);

  const handleSubmit = useCallback(async () => {
    if (!projection) {
      message.error('Не удалось загрузить данные комплекта');
      return;
    }

    let values: FormationModalFormValues;
    try {
      values = await form.validateFields();
    } catch {
      return;
    }

    const validationIssue = validateFormationSubmit({
      form: {
        eventAt: values.formedAt,
        comment: values.comment,
      },
      composition: {
        projection,
        composition,
        rows: validationRows,
      },
    });

    if (validationIssue) {
      message.error(validationIssue.message);
      return;
    }

    try {
      await confirmFormation({
        applicationId,
        body: buildFormationConfirmBody(values.comment, values.formedAt.toISOString()),
      }).unwrap();

      message.success('Комплект сформирован');
      await onSuccess?.();
      onClose();
    } catch (error) {
      message.error(mapClosingPackageApiError(error));
    }
  }, [
    applicationId,
    composition,
    confirmFormation,
    form,
    onClose,
    onSuccess,
    projection,
    validationRows,
  ]);

  return {
    form,
    projection,
    contextView,
    contextLoading,
    submitting: states.confirmFormation.isLoading,
    handleSubmit,
  };
}

export type UseFormationModalResult = ReturnType<typeof useFormationModal>;
